import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import type { Task, User } from '../../types';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import {
  assignedReviewerLabel,
  campaignReviewers,
  reviewerLabel,
} from './reviewAssignment';

interface ReviewAssignmentControlProps {
  task: Task;
  users: User[];
  assigning: boolean;
  onAssign: (reviewerId: string) => Promise<void>;
}

export function ReviewAssignmentControl({
  task,
  users,
  assigning,
  onAssign,
}: ReviewAssignmentControlProps) {
  const reviewers = useMemo(
    () => campaignReviewers(users, task.campaignId),
    [task.campaignId, users],
  );
  const [reviewerId, setReviewerId] = useState(task.reviewerId ?? '');

  useEffect(() => {
    setReviewerId(task.reviewerId ?? '');
  }, [task.id, task.reviewerId]);

  if (task.status !== 'in_review') return null;

  const currentReviewerUnavailable = !!task.reviewerId
    && !reviewers.some((reviewer) => reviewer.uid === task.reviewerId);
  const changed = !!reviewerId && reviewerId !== task.reviewerId;
  const reviewerOptions = [
    ...(currentReviewerUnavailable && task.reviewerId
      ? [{
        value: task.reviewerId,
        label: `${assignedReviewerLabel(task, users)} (not available)`,
        disabled: true,
      }]
      : []),
    ...reviewers.map((reviewer) => ({
      value: reviewer.uid,
      label: reviewerLabel(reviewer),
    })),
  ];

  return (
    <div className="col-span-2 inline-flex min-h-[36px] items-stretch rounded-md border border-gray-300 bg-white sm:col-span-1">
      <SearchableDropdown
        id="browse-task-reviewer"
        options={reviewerOptions}
        value={reviewerId || null}
        onChange={(value) => setReviewerId(value ?? '')}
        disabled={assigning || reviewers.length === 0}
        ariaLabel="Assigned reviewer"
        placeholder={reviewers.length > 0 ? 'Choose a reviewer' : 'No eligible reviewers'}
        searchPlaceholder="Search reviewers"
        preserveOrder
        className="min-w-[15rem] flex-1"
        triggerClassName="min-h-[36px] rounded-r-none border-0 px-2 py-1.5 text-xs text-gray-700"
      />
      <button
        type="button"
        onClick={() => {
          void onAssign(reviewerId);
        }}
        disabled={!changed || assigning}
        title="Assign review"
        className="inline-flex min-w-[5rem] items-center justify-center gap-1 rounded-r-md border-l border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {assigning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
        Assign
      </button>
    </div>
  );
}
